import { faLock } from '@fortawesome/pro-solid-svg-icons';
import type { FilterConfig, FilterOption } from '@supply-mover-2-0/core';
import React from 'react';
import { useTheme, useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { formTheme } from '../../services/ThemeService';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { Text } from '../base/Text';

interface BaseRadioSelectProps {
	filterType: string;
	config: FilterConfig;
	selectedValues: FilterOption[];
	onValuesChange: (values: FilterOption[]) => void;
	onChange?: (values: FilterOption[]) => void; // Support both prop names
	disabled?: boolean;
	isModalOpen?: boolean;
	lockedValues?: FilterOption[];
	size?: 'xs' | 'sm' | 'md' | 'lg';
	layout?: 'sidebar' | 'tray' | 'inline';
	isPreset?: boolean; // New prop to indicate if values are preset
}

/**
 * BaseRadioSelect Component
 *
 * A specialized radio button filter component that displays options with count badges.
 * This component maintains compatibility with the reporting modules that expect
 * BaseRadioSelect specifically.
 */
export const BaseRadioSelect: React.FC<BaseRadioSelectProps> = ({
	config,
	selectedValues,
	onValuesChange,
	onChange,
	disabled = false,
	isModalOpen = false,
	size = 'md',
	layout = 'inline',
	isPreset = false,
}) => {
	const colors = useThemeColors();
	const { colorScheme } = useTheme();
	const { t } = useTranslation();
	const formColors = formTheme.values[colorScheme];
	const selectedValue =
		selectedValues.length > 0 ? selectedValues[0] : config.defaultValue || null;
	const options = config.options || [];

	const handleValueChange = React.useCallback(
		(option: FilterOption) => {
			// Support both onValuesChange and onChange
			if (onValuesChange) {
				onValuesChange([option]);
			} else if (onChange) {
				onChange([option]);
			}
		},
		[onValuesChange, onChange],
	);

	// Auto-select default value if nothing is selected
	React.useEffect(() => {
		if (!selectedValue && config.defaultValue) {
			handleValueChange(config.defaultValue);
		}
	}, [config.defaultValue, selectedValue, handleValueChange]);

	return (
		<div
			className={cn(
				'mb-4',
				(disabled || isModalOpen) && 'opacity-50 pointer-events-none',
			)}
		>
			{/* Label */}
			<div className="flex items-center gap-2 mb-2">
				<Text
					as="label"
					className={cn(
						'font-medium',
						size === 'xs' ? 'text-xs' : 'text-sm',
						colors.classes.textPrimary,
					)}
				>
					{config.label}
					{config.required && (
						<Text as="span" className={cn(colors.classes.textDanger, 'ml-1')}>
							*
						</Text>
					)}
				</Text>
				{isPreset && (
					<Icon
						icon={faLock}
						size={size === 'xs' ? 'xs' : 'sm'}
						className={colors.classes.textSecondary}
					/>
				)}
			</div>

			{/* Radio Button Group - Vertical Layout */}
			<div className={cn('flex flex-col', size === 'xs' ? 'gap-1' : 'gap-2')}>
				{options.map((option) => (
					<div
						key={option.id}
						onClick={() => {
							if (!(isModalOpen || disabled)) {
								handleValueChange(option);
							}
						}}
						className={cn(
							'flex items-center cursor-pointer rounded-md transition-all w-full',
							size === 'xs'
								? 'px-2 py-1'
								: size === 'sm'
									? 'px-2.5 py-1.5'
									: 'px-3 py-2',
							selectedValue?.id === option.id
								? 'font-semibold'
								: 'hover:opacity-80',
							(isModalOpen || disabled) && 'cursor-not-allowed',
						)}
						role="radio"
						aria-checked={selectedValue?.id === option.id}
						tabIndex={0}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();

								if (!(isModalOpen || disabled)) {
									handleValueChange(option);
								}
							}
						}}
					>
						{/* Radio circle - React controlled */}
						<div
							className={cn(
								'w-4 h-4 rounded-full border-2 flex-shrink-0 mr-2 flex items-center justify-center transition-colors duration-200',
								selectedValue?.id === option.id
									? 'bg-blue-600 border-blue-600 dark:bg-blue-500 dark:border-blue-500'
									: 'bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-600',
							)}
						>
							{selectedValue?.id === option.id && (
								<div className="w-1.5 h-1.5 rounded-full bg-white" />
							)}
						</div>

						{/* Label */}
						<Text
							className={cn(
								size === 'xs'
									? 'text-xs'
									: size === 'sm'
										? 'text-sm'
										: 'text-sm',
								selectedValue?.id === option.id
									? colors.classes.textPrimary
									: colors.classes.textSecondary,
							)}
						>
							{option.label}
						</Text>

						{/* Count Badge - Only show if not hideCount and count is defined */}
						{!config.hideCount && option.count != null && (
							<Text
								className={cn(
									'rounded-full ml-2 font-medium',
									size === 'xs' ? 'px-1.5 py-0 text-xs' : 'px-2 py-0.5 text-xs',
									colors.classes.textSecondary,
								)}
							>
								({option.count})
							</Text>
						)}
					</div>
				))}
			</div>
		</div>
	);
};
